import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminTenants, useCreateSubscription, useUpdateSubscription } from '@/hooks/useAdmin';
import { TenantWithSubscription, SubscriptionCreate, SubscriptionUpdate } from '@/services/admin';
import { subscriptionsService } from '@/services/subscriptions';
import { useFeatures } from '@/hooks/useFeatures';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { formatPrice, parseARNumber } from '@/utils/format';

// Suma 1 mes a una fecha YYYY-MM-DD preservando el día (clamp a fin de mes)
const addOneMonthStr = (ymd: string): string => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  let ny = y;
  let nm = m + 1;
  if (nm > 12) { nm = 1; ny = y + 1; }
  const lastDay = new Date(ny, nm, 0).getDate();
  const nd = Math.min(d, lastDay);
  return `${ny}-${String(nm).padStart(2, '0')}-${String(nd).padStart(2, '0')}`;
};
import {
  CreditCard,
  Search,
  Plus,
  Edit2,
  Building2,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  Send,
} from 'lucide-react';

export default function AdminSubscriptions() {
  const { data: tenants, isLoading } = useAdminTenants();
  const createSubscription = useCreateSubscription();
  const updateSubscription = useUpdateSubscription();
  const queryClient = useQueryClient();
  const { data: features } = useFeatures();
  const mpEnabled = features?.mp_enabled === true;

  const confirmPaymentMutation = useMutation({
    mutationFn: (subscriptionId: number) => subscriptionsService.confirmPayment(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-payments'] });
      toast.success('Pago confirmado');
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'No se pudo confirmar'),
  });

  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantWithSubscription | null>(null);
  const [formData, setFormData] = useState<{
    plan: string;
    status: string;
    price_monthly: number;
    payment_status: string;
    start_date: string;        // YYYY-MM-DD
    next_payment_date: string; // YYYY-MM-DD
    notes: string;
  }>({
    plan: 'basic',
    status: 'active',
    price_monthly: 40000,
    payment_status: 'pending',
    start_date: '',
    next_payment_date: '',
    notes: '',
  });

  const tenantsWithSub = tenants?.filter((t) => t.subscription);
  const tenantsWithoutSub = tenants?.filter((t) => !t.subscription);

  const filteredTenants = tenants?.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(search.toLowerCase()) ||
      tenant.slug.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenCreate = (tenant: TenantWithSubscription) => {
    setEditingTenant(tenant);
    // Default: inicio hoy; primer vencimiento = mismo día del mes siguiente
    const today = new Date().toISOString().slice(0, 10);
    setFormData({
      plan: 'basic',
      status: 'active',
      price_monthly: 40000,
      payment_status: 'pending',
      start_date: today,
      next_payment_date: addOneMonthStr(today),
      notes: '',
    });
    setShowModal(true);
  };

  const handleOpenEdit = (tenant: TenantWithSubscription) => {
    setEditingTenant(tenant);
    if (tenant.subscription) {
      setFormData({
        plan: tenant.subscription.plan,
        status: tenant.subscription.status,
        price_monthly: Number(tenant.subscription.price_monthly),
        payment_status: tenant.subscription.payment_status,
        start_date: tenant.subscription.start_date
          ? tenant.subscription.start_date.slice(0, 10)
          : '',
        next_payment_date: tenant.subscription.next_payment_date
          ? tenant.subscription.next_payment_date.slice(0, 10)
          : '',
        notes: tenant.subscription.notes || '',
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTenant) return;

    if (editingTenant.subscription) {
      const currentPrice = Number(editingTenant.subscription.price_monthly);
      const newPrice = Number(formData.price_monthly);
      const priceChanged = currentPrice !== newPrice;
      const hasMp = !!editingTenant.subscription.mp_preapproval_id;

      try {
        // Si cambió el precio, usar el endpoint dedicado que sincroniza con MP
        if (priceChanged) {
          await subscriptionsService.updatePrice(editingTenant.subscription.id, {
            new_price: newPrice,
            reason: formData.notes || undefined,
          });
          if (hasMp) {
            toast.success('Precio actualizado y sincronizado con Mercado Pago');
          }
        }

        // Actualizar el resto de campos (plan, status, payment_status, start_date, next_payment_date, notes)
        const { price_monthly, next_payment_date, start_date, ...otherFields } = formData;
        await updateSubscription.mutateAsync({
          id: editingTenant.subscription.id,
          data: {
            ...otherFields,
            start_date: start_date ? new Date(start_date + 'T00:00:00').toISOString() : undefined,
            next_payment_date: next_payment_date ? new Date(next_payment_date + 'T00:00:00').toISOString() : null,
          } as SubscriptionUpdate,
        });
      } catch (err: any) {
        toast.error(err.response?.data?.detail || 'Error al guardar');
        return;
      }
    } else {
      // Create new
      const { next_payment_date, start_date, ...rest } = formData;
      await createSubscription.mutateAsync({
        tenant_id: editingTenant.id,
        ...rest,
        start_date: start_date ? new Date(start_date + 'T00:00:00').toISOString() : undefined,
        next_payment_date: next_payment_date ? new Date(next_payment_date + 'T00:00:00').toISOString() : null,
      } as SubscriptionCreate);
    }

    setShowModal(false);
    setEditingTenant(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Activa</span>;
      case 'trial':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Prueba</span>;
      case 'suspended':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Suspendida</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Cancelada</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{status}</span>;
    }
  };

  const getPaymentBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm">Pagado</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-1 text-amber-600">
            <Clock className="h-4 w-4" />
            <span className="text-sm">Pendiente</span>
          </div>
        );
      case 'overdue':
        return (
          <div className="flex items-center gap-1 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm">Vencido</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suscripciones</h1>
          <p className="text-gray-500">Gestiona las suscripciones de los almacenes</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tenantsWithSub?.length || 0}</p>
              <p className="text-xs text-gray-500">Con suscripción</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{tenantsWithoutSub?.length || 0}</p>
              <p className="text-xs text-gray-500">Sin suscripción</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenantsWithSub?.filter((t) => t.subscription?.payment_status === 'paid').length || 0}
              </p>
              <p className="text-xs text-gray-500">Al día</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tenantsWithSub?.filter((t) => t.subscription?.payment_status === 'overdue').length || 0}
              </p>
              <p className="text-xs text-gray-500">Vencidos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants without subscription */}
      {tenantsWithoutSub && tenantsWithoutSub.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <h3 className="font-semibold text-amber-900 mb-3">Almacenes sin suscripción</h3>
          <div className="flex flex-wrap gap-2">
            {tenantsWithoutSub.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => handleOpenCreate(tenant)}
                className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
              >
                <Building2 className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">{tenant.name}</span>
                <Plus className="h-4 w-4 text-amber-600" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Subscriptions List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Suscripciones Activas</h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : filteredTenants && filteredTenants.filter((t) => t.subscription).length > 0 ? (
          <div className="divide-y divide-gray-100">
            {filteredTenants
              .filter((t) => t.subscription)
              .map((tenant) => (
                <div
                  key={tenant.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {getStatusBadge(tenant.subscription!.status)}
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full capitalize">
                              Plan {tenant.subscription!.plan}
                            </span>
                            {mpEnabled && tenant.subscription!.mp_status === 'authorized' && (
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" /> MP autorizado
                              </span>
                            )}
                            {mpEnabled && tenant.subscription!.mp_status === 'pending' && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                                <Clock className="h-3 w-3" /> MP pendiente
                              </span>
                            )}
                            {mpEnabled && tenant.subscription!.mp_status === 'cancelled' && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">MP cancelado</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {formatPrice(tenant.subscription!.price_monthly)}
                          </p>
                          <p className="text-sm text-gray-500">/mes</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
                        {getPaymentBadge(tenant.subscription!.payment_status)}
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>Desde {formatDate(tenant.subscription!.start_date)}</span>
                        </div>
                        {tenant.subscription!.next_payment_date && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                            <span>Próximo pago: {formatDate(tenant.subscription!.next_payment_date)}</span>
                          </div>
                        )}
                        {tenant.subscription!.payment_reported_at && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            <Send className="h-3 w-3" />
                            Cliente reportó pago
                          </span>
                        )}
                      </div>

                      {/* Acciones de pago */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {tenant.subscription!.payment_status !== 'paid' && (
                          <button
                            onClick={() => {
                              const amount = formatPrice(tenant.subscription!.price_monthly);
                              if (confirm(`¿Confirmás que recibiste el pago de ${amount} de "${tenant.name}"?`)) {
                                confirmPaymentMutation.mutate(tenant.subscription!.id);
                              }
                            }}
                            disabled={confirmPaymentMutation.isPending}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Confirmar pago recibido
                          </button>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleOpenEdit(tenant)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                      title="Editar"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No hay suscripciones</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTenant(null);
        }}
        title={editingTenant?.subscription ? 'Editar Suscripción' : 'Nueva Suscripción'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Almacén</p>
            <p className="font-semibold text-gray-900">{editingTenant?.name}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="basic">Básico</option>
                <option value="professional">Profesional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="trial">Prueba</option>
                <option value="active">Activa</option>
                <option value="suspended">Suspendida</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio Mensual (ARS)</label>
              <CurrencyInput
                value={formData.price_monthly}
                onChange={(v) => setFormData({ ...formData, price_monthly: parseARNumber(v) ?? 0 })}
                focusRingClassName="focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado de Pago</label>
              <select
                value={formData.payment_status}
                onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="pending">Pendiente</option>
                <option value="paid">Pagado</option>
                <option value="overdue">Vencido</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de inicio de la suscripción
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => {
                  const start = e.target.value;
                  // Al elegir el inicio, el próximo pago se ancla al mismo día del mes siguiente
                  setFormData({
                    ...formData,
                    start_date: start,
                    next_payment_date: start ? addOneMonthStr(start) : formData.next_payment_date,
                  });
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vencimiento del próximo pago
              </label>
              <input
                type="date"
                value={formData.next_payment_date}
                onChange={(e) => setFormData({ ...formData, next_payment_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            El ciclo se ancla al día de inicio: si arranca el 15, el próximo pago vence el 15 del mes siguiente.
            Al confirmar cada pago, avanza automáticamente al mismo día del mes siguiente.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingTenant(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={createSubscription.isPending || updateSubscription.isPending}
            >
              {editingTenant?.subscription ? 'Guardar Cambios' : 'Crear Suscripción'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
